import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, TrendingUp, CheckCircle2, Star, ArrowLeft, ArrowRight, Mic2, GraduationCap, MapPin } from 'lucide-react';
import SEO from '../components/SEO';

const FOTO_ADEILSON = 'https://customer-assets.emergentagent.com/job_behringer-school/artifacts/i92csdl6_IMG_4848.JPEG';
const LOGO_BEHRINGER = 'https://customer-assets.emergentagent.com/job_behringer-school/artifacts/r5m1uacd_LOGO%20BEHRINGER%20PNG%20BRANCA.png';

const personJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  mainEntity: {
    '@type': 'Person',
    name: 'Adeilson Chagas',
    jobTitle: 'Técnico de Áudio e Endorsee Oficial Behringer Brasil',
    description:
      'Técnico de áudio profissional, endorsee oficial da Behringer no Brasil e instrutor dos treinamentos oficiais WING Summit. Fundador da WING Academy Brasil.',
    image: FOTO_ADEILSON,
    url: 'https://wingacademybrasil.com.br/sobre',
    worksFor: { '@type': 'Organization', name: 'WING Academy Brasil', url: 'https://wingacademybrasil.com.br' },
    knowsAbout: ['Behringer WING', 'Mixagem de áudio ao vivo', 'Sonorização profissional', 'FOH', 'Monitor', 'Roteamento de áudio digital'],
    sameAs: ['https://www.instagram.com/wingacademybrasil', 'https://www.youtube.com/@wingacademybrasil'],
  },
};

const CREDENCIAIS = [
  { icon: Star, titulo: 'Endorsee Oficial Behringer Brasil', desc: 'Reconhecimento oficial da marca como especialista e representante técnico da linha WING no país.' },
  { icon: Mic2, titulo: 'Instrutor dos eventos WING Summit', desc: 'Responsável pelos treinamentos oficiais Behringer que percorrem o Brasil capacitando técnicos de áudio.' },
  { icon: GraduationCap, titulo: 'Fundador da WING Academy Brasil', desc: 'Criador da primeira mentoria brasileira 100% dedicada à console Behringer WING — Full, Compact e Rack.' },
  { icon: MapPin, titulo: 'Atuação nacional em FOH e Monitor', desc: 'Mais de 15 anos operando som ao vivo em igrejas, eventos e turnês por todo o Brasil.' },
];

const TRAJETORIA = [
  { ano: 'Início', texto: 'Começa a carreira como técnico de som ao vivo, operando consoles analógicas e digitais em igrejas e eventos.' },
  { ano: '15+ anos', texto: 'Consolida-se como referência em áudio profissional, com atuação em FOH, monitor e sonorização de grandes eventos.' },
  { ano: 'Endorsee', texto: 'É reconhecido oficialmente pela Behringer Brasil como endorsee e especialista na console WING.' },
  { ano: 'WING Summit', texto: 'Assume os treinamentos oficiais WING Summit, capacitando centenas de profissionais em workshops pelo Brasil.' },
  { ano: 'Hoje', texto: 'Lidera a WING Academy Brasil, com mentorias em grupo, treinamentos individuais e uma comunidade ativa de operadores WING.' },
];

export default function Sobre() {
  return (
    <>
      <SEO
        title="Sobre Adeilson Chagas — Endorsee Oficial Behringer Brasil"
        description="Conheça Adeilson Chagas: técnico de áudio com 15+ anos de experiência, endorsee oficial Behringer Brasil, instrutor do WING Summit e fundador da WING Academy Brasil."
        path="/sobre"
        image={FOTO_ADEILSON}
        breadcrumbs={[{ name: 'Sobre Adeilson Chagas', path: '/sobre' }]}
        jsonLd={[personJsonLd]}
      />
      <div data-testid="sobre-page" className="min-h-screen bg-black text-white">
        {/* Top bar */}
        <div className="container mx-auto px-4 pt-6">
          <Link
            to="/"
            data-testid="sobre-back-link"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-yellow-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar ao início
          </Link>
        </div>

        {/* Hero */}
        <section className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-yellow-400 font-mono text-sm tracking-widest uppercase mb-4">Sobre o instrutor</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
                Adeilson <span className="text-yellow-400">Chagas</span>
              </h1>
              <p className="text-lg text-yellow-400/90 mb-6">Técnico de Áudio • Endorsee Oficial Behringer Brasil</p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Com mais de 15 anos de estrada no áudio profissional, Adeilson Chagas é hoje uma das maiores
                referências nacionais na console Behringer WING. Reconhecido oficialmente pela marca como
                endorsee, ele é o instrutor responsável pelos treinamentos oficiais <strong className="text-white">WING Summit</strong>,
                que percorrem o Brasil capacitando técnicos e operadores de som.
              </p>
              <p className="text-gray-300 leading-relaxed mb-8">
                Fundador da <strong className="text-white">WING Academy Brasil</strong>, Adeilson uniu experiência
                prática de palco com didática acessível para criar a primeira mentoria brasileira 100% dedicada à
                WING — do roteamento básico às técnicas avançadas de FOH, monitor, FX e mixbus.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/#inscricao"
                  data-testid="sobre-cta-mentoria"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 px-8 py-3 font-semibold text-black transition-colors hover:bg-yellow-300"
                >
                  Garantir Minha Vaga <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/quiz"
                  data-testid="sobre-cta-quiz"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-700 px-8 py-3 font-semibold text-white transition-colors hover:border-yellow-400 hover:text-yellow-400"
                >
                  Testar meu nível na WING
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-5 -right-5 bg-yellow-500 p-5 rounded-xl shadow-2xl z-20 text-center">
                <Star className="w-7 h-7 text-black mx-auto mb-1" />
                <div className="text-xl font-bold text-black leading-none">Endorsee</div>
                <div className="text-xs text-black/80 mt-1">Oficial Behringer</div>
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden relative border border-gray-800">
                <img src={FOTO_ADEILSON} alt="Adeilson Chagas — Endorsee Oficial Behringer Brasil" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm py-4 px-6 flex items-center justify-center border-t border-yellow-500/30">
                  <img src={LOGO_BEHRINGER} alt="Behringer" className="h-8 w-auto" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="border-y border-gray-800 bg-gray-950/60">
          <div className="container mx-auto px-4 py-10">
            <div className="max-w-4xl mx-auto grid grid-cols-3 gap-6 text-center">
              <div data-testid="sobre-stat-anos">
                <Award className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <div className="text-3xl font-bold">15+</div>
                <div className="text-xs text-gray-400 mt-1">Anos de experiência<br />no áudio profissional</div>
              </div>
              <div data-testid="sobre-stat-alunos">
                <Users className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <div className="text-3xl font-bold">100+</div>
                <div className="text-xs text-gray-400 mt-1">Profissionais<br />capacitados</div>
              </div>
              <div data-testid="sobre-stat-eventos">
                <TrendingUp className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <div className="text-3xl font-bold">5</div>
                <div className="text-xs text-gray-400 mt-1">Eventos oficiais<br />Behringer (WING Summit)</div>
              </div>
            </div>
          </div>
        </section>

        {/* Credenciais */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-base md:text-lg font-mono uppercase tracking-widest text-yellow-400 mb-2">Credenciais</h2>
            <p className="text-3xl md:text-4xl font-bold mb-10">Por que aprender com o Adeilson?</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {CREDENCIAIS.map((c, i) => (
                <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-xl p-6 hover:border-yellow-500/40 transition-colors">
                  <c.icon className="w-7 h-7 text-yellow-400 mb-3" />
                  <h3 className="font-semibold text-white mb-1.5">{c.titulo}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trajetória */}
        <section className="container mx-auto px-4 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-base md:text-lg font-mono uppercase tracking-widest text-yellow-400 mb-2">Trajetória</h2>
            <p className="text-3xl md:text-4xl font-bold mb-10">Do palco à sala de aula</p>
            <div className="space-y-0">
              {TRAJETORIA.map((t, i) => (
                <div key={i} className="flex gap-5">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-yellow-400 mt-1.5 flex-shrink-0"></div>
                    {i < TRAJETORIA.length - 1 && <div className="w-px flex-1 bg-gray-800"></div>}
                  </div>
                  <div className="pb-8">
                    <div className="text-yellow-400 font-mono text-sm font-semibold mb-1">{t.ano}</div>
                    <p className="text-gray-300 text-sm leading-relaxed">{t.texto}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="container mx-auto px-4 pb-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/30 rounded-2xl p-8 md:p-12 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-300">Vagas limitadas por turma • Garantia incondicional na 1ª aula</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Pronto para dominar a Console WING?</h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Nova turma da Mentoria em Grupo com início em breve — 4 aulas ao vivo + mais de 20 aulas gravadas. Vagas limitadas.
            </p>
            <a
              href="/#inscricao"
              data-testid="sobre-cta-final"
              className="inline-flex items-center gap-2 rounded-full bg-yellow-400 px-10 py-4 font-bold text-black transition-colors hover:bg-yellow-300"
            >
              Garantir Minha Vaga <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
